import React, { useContext, useEffect, useMemo } from 'react'
import BannerInnerOne from './child/BannerInnerOne'
import BusinessOverview from './child/BusinessOverview'
import RecentInvoices from './child/RecentInvoices'
import { ConfigContext } from '../context/GlobalContext'
import DashboardChart from './DashboardChart'
import * as actionType from '../context/actions'
import SaleReturnChart from './child/SaleReturnChart'
import ProfitChart from './child/ProfitChart'
import CustomerLedgerHighlight from './child/CustomerLedgerHighlight'
import AssignMemberHighlight from './child/AssignMemberHighlight'
import Currancy from '../Utils/currency.json'

const DashBoardLayerSeven = () => {

    const { state, dispatch } = useContext(ConfigContext);

    useEffect(() => {
        dispatch({ type: actionType.ACTIVEFILTER, payload: 'All' });
    }, [dispatch]);

    // ── Step 1: Filter by selected business ─────────────────────────────────
    const bizFilteredState = useMemo(() => {
        const ownBusinesses = (state?.BUSINESSES || []).filter(b => !b.isShared);
        const ownBusinessIds = new Set(ownBusinesses.map(b => String(b.companyId)));

        const selectedBiz = state?.SELECTEDBUSINESS;
        const hasSpecificBusiness =
            selectedBiz && selectedBiz !== 'null' && ownBusinessIds.has(String(selectedBiz));

        const activeIds = hasSpecificBusiness
            ? new Set([String(selectedBiz)])
            : ownBusinessIds;

        const filteredInvoices = (state?.INVOICES || []).filter(inv =>
            activeIds.has(String(inv.companyId))
        );
        const filteredProformas = (state?.PROFORMAINVOICES || []).filter(inv =>
            activeIds.has(String(inv.companyId))
        );
        const filteredQuotations = (state?.QUOTATION || []).filter(q =>
            activeIds.has(String(q.companyId))
        );
        const filteredReturns = (state?.SALERETURN || []).filter(sr =>
            activeIds.has(String(sr.companyId))
        );
        const filteredCustomers = (state?.CUSTOMERS || []).filter(c =>
            activeIds.has(String(c.companyId))
        );
        const filteredItems = (state?.ITEMS || []).filter(i =>
            activeIds.has(String(i.companyId))
        );
        const filteredExpense = (state?.EXPENSE || []).filter(e =>
            activeIds.has(String(e.companyId))
        );
        const filteredBusinesses = hasSpecificBusiness
            ? ownBusinesses.filter(b => String(b.companyId) === String(selectedBiz))
            : ownBusinesses;

        return {
            ...state,
            BUSINESSES: filteredBusinesses,
            INVOICES: filteredInvoices,
            PROFORMAINVOICES: filteredProformas,
            QUOTATION: filteredQuotations,
            SALERETURN: filteredReturns,
            CUSTOMERS: filteredCustomers,
            ITEMS: filteredItems,
            EXPENSE: filteredExpense,
            SELECTEDBUSINESS: hasSpecificBusiness ? selectedBiz : 'null'
        };
    }, [
        state?.BUSINESSES,
        state?.INVOICES,
        state?.PROFORMAINVOICES,
        state?.QUOTATION,
        state?.SALERETURN,
        state?.CUSTOMERS,
        state?.ITEMS,
        state?.EXPENSE,
        state?.SELECTEDBUSINESS,
        state?.ACTIVEFILTER
    ]);

    // ── Step 2: Apply currency filter + update symbol/code for child components
    const dashboardState = useMemo(() => {
        const currency = state?.DASHBOARD_CURRENCY;

        // Resolve symbol & code for the selected dashboard currency
        const matched = currency
            ? Currancy?.CountryCurrencyResponse?.find(c => c.currencyCode === currency)
            : null;
        const resolvedSymbol = matched?.currencytSymbol || bizFilteredState.SYMBOL;
        const resolvedCode   = matched?.currencyCode    || bizFilteredState.CURRENCYCODE;

        if (!currency) return bizFilteredState;

        return {
            ...bizFilteredState,
            // Override symbol/code so child components display correctly
            SYMBOL: resolvedSymbol,
            CURRENCYCODE: resolvedCode,
            INVOICES: (bizFilteredState.INVOICES || []).filter(
                inv => (inv.currency || bizFilteredState.CURRENCYCODE) === currency
            ),
            PROFORMAINVOICES: (bizFilteredState.PROFORMAINVOICES || []).filter(
                inv => (inv.currency || bizFilteredState.CURRENCYCODE) === currency
            ),
            QUOTATION: (bizFilteredState.QUOTATION || []).filter(
                q => (q.currency || bizFilteredState.CURRENCYCODE) === currency
            ),
            SALERETURN: (bizFilteredState.SALERETURN || []).filter(
                sr => (sr.currency || bizFilteredState.CURRENCYCODE) === currency
            ),
            EXPENSE: (bizFilteredState.EXPENSE || []).filter(
                e => (e.currency || bizFilteredState.CURRENCYCODE) === currency
            ),
        };
    }, [bizFilteredState, state?.DASHBOARD_CURRENCY]);

    const contextValue = useMemo(() => ({ state: dashboardState, dispatch }), [dashboardState, dispatch]);

    return (
        <ConfigContext.Provider value={contextValue}>
            <div className="row gy-4">

                {/* Row 1: Business Overview + Main Chart */}
                <div className="col-12">
                    <div className="row gy-4">
                        <div className="col-12 col-xl-8">
                            {dashboardState.BUSINESSES?.length === 0 && (
                                <BannerInnerOne />
                            )}
                            <BusinessOverview state={dashboardState} dispatch={dispatch} isdashboard={"true"} />
                        </div>
                        <div className="col-12 col-xl-4">
                            <DashboardChart />
                        </div>
                    </div>
                </div>

                {/* Row 2: Highlights Sidebar & Revenue Statistics */}
                <div className="col-12">
                    <div className="row gy-4">
                        {/* Left: Highlights Sidebar */}
                        <div className="col-12 col-xl-3">
                            <div className="d-flex flex-column h-100 gap-4">
                                <div className="flex-fill">
                                    <CustomerLedgerHighlight />
                                </div>
                                <div className="flex-fill">
                                    <AssignMemberHighlight />
                                </div>
                            </div>
                        </div>

                        {/* Right: Profit / Revenue Chart */}
                        <div className="col-12 col-xl-9">
                            <ProfitChart InvoicesData={dashboardState.INVOICES} />
                        </div>
                    </div>
                </div>

                {/* Row 3: Recent Activity & secondary Analytics */}
                <div className="col-12">
                    <div className="row gy-4">
                        <div className="col-12 col-xl-8">
                            <RecentInvoices InvoicesData={dashboardState.INVOICES?.slice(0, 5)} />
                        </div>
                        <div className="col-12 col-xl-4">
                            <SaleReturnChart state={dashboardState} />
                        </div>
                    </div>
                </div>
            </div>
        </ConfigContext.Provider>
    );
};

export default DashBoardLayerSeven